/**
 * My Project Requests — W3-A · B7.
 *
 * Route: /workflows/my-requests
 * Perm : workflow:read_own
 *
 * Lists the caller's Project Requests (workflow instances the caller
 * initiated). Grouped by status via a filter chip strip. Clicking a
 * row opens the shared RequestDetailDrawer, which lets the caller
 * withdraw a pending request.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, ClipboardText, Plus } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { WorkflowStatusBadge, STATUS_LABELS, formatDT } from "./workflowUi";
import RequestDetailDrawer from "./components/RequestDetailDrawer";
import ProjectCreateDialog from "../projects/components/ProjectCreateDialog";
import RequestSubmittedDialog from "../projects/components/RequestSubmittedDialog";
import { PageLoading } from "../projects/components/ui";


const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending Approval" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "withdrawn", label: "Withdrawn" },
    { key: "override_active", label: "Emergency Created" },
];


function statusLabel(status) {
    return STATUS_LABELS[status]?.label || status;
}


export default function MyRequests() {
    const { user } = useAuth();
    const perms = new Set(user?.permissions || []);
    const canInitiate = perms.has("project:initiate");

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [openInstanceId, setOpenInstanceId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [submittedRequest, setSubmittedRequest] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("mine", "true");
            params.set("workflow_code", "project_initiation");
            params.set("limit", "100");
            if (filter !== "all") params.set("status", filter);
            const r = await api.get(`/workflows/instances?${params}`);
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load your requests");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.trim().toLowerCase();
        return items.filter((it) => {
            const name = (it.payload?.name || "").toLowerCase();
            const ref = (it.workflow_instance_id || "").toLowerCase();
            return name.includes(needle) || ref.includes(needle);
        });
    }, [items, q]);

    return (
        <div className="space-y-6" data-testid="my-requests-page">
            <PageHeader
                eyebrow="Delivery"
                title="My Project Requests"
                description="Project Requests you have submitted. Track approval progress, withdraw pending requests, and see reviewer feedback."
                actions={
                    <div className="flex items-center gap-2">
                        {canInitiate ? (
                            <Button
                                onClick={() => setCreateOpen(true)}
                                data-testid="my-requests-initiate-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Initiate Project
                            </Button>
                        ) : null}
                        <Button
                            variant="outline"
                            onClick={load}
                            disabled={loading}
                            data-testid="my-requests-refresh-btn"
                        >
                            <ArrowClockwise
                                className={"w-4 h-4 mr-1 " + (loading ? "animate-spin" : "")}
                            />
                            Refresh
                        </Button>
                    </div>
                }
            />

            <div className="flex flex-wrap items-center gap-2">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={
                            "px-3 py-1 rounded-full border text-sm transition " +
                            (filter === f.key
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                        }
                        data-testid={`my-requests-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
                <div className="flex-1 min-w-[200px]">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by project name or reference…"
                        data-testid="my-requests-search"
                    />
                </div>
            </div>

            {loading ? (
                <PageLoading
                    label="Loading your requests…"
                    testId="my-requests-loading"
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<ClipboardText className="w-6 h-6" />}
                    title={
                        items.length === 0
                            ? "No Project Requests yet"
                            : "No matches"
                    }
                    description={
                        items.length === 0
                            ? "Submit a Project Request to get started — the Active Project will be created once an authorised approver approves it."
                            : "Try clearing the filters or search."
                    }
                    action={
                        items.length === 0 && canInitiate ? (
                            <Button
                                onClick={() => setCreateOpen(true)}
                                data-testid="my-requests-empty-initiate-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Initiate Project
                            </Button>
                        ) : null
                    }
                />
            ) : (
                <div
                    className="overflow-x-auto border border-slate-200 rounded-md"
                    data-testid="my-requests-table-wrapper"
                >
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Reference</th>
                                <th className="text-left px-3 py-2">Project</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Submitted</th>
                                <th className="text-left px-3 py-2">Last update</th>
                            </tr>
                        </thead>
                        <tbody data-testid="my-requests-table-body">
                            {filtered.map((it) => (
                                <tr
                                    key={it.workflow_instance_id}
                                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => setOpenInstanceId(it.workflow_instance_id)}
                                    data-testid={`my-requests-row-${it.workflow_instance_id}`}
                                >
                                    <td className="px-3 py-2 font-mono text-xs text-slate-700">
                                        {it.workflow_instance_id}
                                    </td>
                                    <td className="px-3 py-2 text-slate-900">
                                        {it.payload?.name || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700 capitalize">
                                        {it.payload?.project_type || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                        <WorkflowStatusBadge status={it.status} />
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 text-xs">
                                        {formatDT(it.created_at)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 text-xs">
                                        {formatDT(
                                            it.updated_at
                                            || it.approved_at
                                            || it.rejected_at
                                            || it.withdrawn_at
                                            || it.created_at,
                                        )}
                                        <span className="sr-only">{statusLabel(it.status)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <RequestDetailDrawer
                open={!!openInstanceId}
                instanceId={openInstanceId}
                onClose={() => setOpenInstanceId(null)}
                canWithdraw={true}
                onWithdrawn={() => {
                    setOpenInstanceId(null);
                    load();
                }}
            />

            <ProjectCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSubmitted={(pendingRequest) => {
                    setCreateOpen(false);
                    setSubmittedRequest(pendingRequest);
                    load();
                }}
            />
            <RequestSubmittedDialog
                open={!!submittedRequest}
                request={submittedRequest}
                onClose={() => setSubmittedRequest(null)}
            />
        </div>
    );
}
